/**
 * AdminQuoteDetail.jsx — Fiche détail CRM + Quote builder + envoi + actions
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';
import { toast } from 'sonner';
import { formatApiError } from '../../utils/apiErrors';
import {
  ArrowLeft, Send, Download, FileText, User, MessageCircle, Clock, Tag,
  Building2, Check, AlertTriangle, Loader2, Copy, Flame, Sparkles,
} from 'lucide-react';

const API = getApiUrl();

const STATUS_META = {
  pending_review: { label: 'En analyse', color: '#D97706', bg: '#FEF3C7' },
  quoted: { label: 'Devis émis', color: '#047857', bg: '#D1FAE5' },
  rejected: { label: 'Refusée', color: '#991B1B', bg: '#FEE2E2' },
  cancelled: { label: 'Annulée', color: '#6B7280', bg: '#F3F4F6' },
};
const QUOTE_STATUS_META = {
  draft: { label: 'Brouillon', color: '#6B7280', bg: '#F3F4F6' },
  sent: { label: 'Envoyé', color: '#1E40AF', bg: '#DBEAFE' },
  viewed: { label: 'Consulté', color: '#92400E', bg: '#FEF3C7' },
  accepted_online: { label: 'Signé en ligne', color: '#047857', bg: '#D1FAE5' },
  bdc_received: { label: 'BDC reçu', color: '#047857', bg: '#D1FAE5' },
  accepted: { label: 'Commande validée', color: '#065F46', bg: '#A7F3D0' },
  rejected: { label: 'Refusé', color: '#991B1B', bg: '#FEE2E2' },
  expired: { label: 'Expiré', color: '#6B7280', bg: '#F3F4F6' },
};

export default function AdminQuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [newComment, setNewComment] = useState('');

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/enterprise/quote-requests/${id}`, { headers });
      if (!res.ok) throw new Error('Introuvable');
      setData(await res.json());
    } catch (e) { toast.error(formatApiError(e, 'Chargement impossible')); } finally { setLoading(false); }
  };

  const updatePriority = async (priority) => {
    await fetch(`${API}/admin/enterprise/quote-requests/${id}/priority`, {
      method: 'PUT', headers, body: JSON.stringify({ commercial_priority: priority }),
    });
    toast.success('Priorité mise à jour'); fetchData();
  };
  const updateAssignment = async (next_action) => {
    await fetch(`${API}/admin/enterprise/quote-requests/${id}/assign`, {
      method: 'PUT', headers, body: JSON.stringify({ next_action }),
    });
    toast.success('Assignation mise à jour'); fetchData();
  };
  const addComment = async () => {
    if (!newComment.trim()) return;
    await fetch(`${API}/admin/enterprise/quote-requests/${id}/comments`, {
      method: 'POST', headers, body: JSON.stringify({ body: newComment }),
    });
    setNewComment(''); toast.success('Commentaire ajouté'); fetchData();
  };

  if (loading || !data) return <div className="p-10 text-[#6B7280]">Chargement…</div>;

  const qreq = data.quote_request;
  const comments = data.comments;
  const quotes = data.quotes;
  const timeline = data.timeline;
  const contact = qreq.contact || {};
  const payload = qreq.payload || {};
  const status = STATUS_META[qreq.status] || {};
  const latestQuote = quotes[0];

  return (
    <div className="theme-light min-h-screen bg-[#FBF9F3] pb-20">
      <div className="border-b border-[#E8E5DE] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/admin/enterprise-quotes" className="inline-flex items-center gap-1.5 text-[#6B7280] hover:text-[#111827] text-sm mb-4" data-testid="admin-quote-back">
            <ArrowLeft className="w-4 h-4" /> Retour à la liste
          </Link>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="uppercase text-[11px] tracking-[0.25em] text-[#92710A] font-medium">{qreq.type === 'ia' ? 'Coaching IA' : 'Coaching Humain'}</p>
                {qreq.request_number && (
                  <span className="text-[11px] font-mono font-medium text-[#111827] bg-[#FBF9F3] border border-[#E8DFC8] px-2 py-0.5 rounded-full">
                    {qreq.request_number}
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#111827] mt-1">{contact.company_name}</h1>
              <p className="text-sm text-[#4B5563] mt-1">{contact.hr_contact_name} · {contact.hr_contact_email}</p>
            </div>
            <span className="inline-flex items-center text-[11px] uppercase tracking-wider font-medium px-3 py-1.5 rounded-full"
              style={{ color: status.color, backgroundColor: status.bg }} data-testid="admin-quote-status-badge">
              {status.label}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — Request details + Quote */}
        <div className="lg:col-span-2 space-y-4">
          {/* Demande */}
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-quote-request-details">
            <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#92710A]" /> Demande initiale</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <KV label="Collaborateurs" value={payload.collaborators_count} />
              <KV label="Taille" value={contact.company_size?.toUpperCase()} />
              {qreq.type === 'ia' ? (
                <>
                  <KV label="Niveau" value={payload.current_management_level} />
                  <KV label="Besoin" value={payload.need_type} />
                  <KV label="Durée souhaitée" value={`${payload.desired_duration_months} mois`} />
                  <KV label="Sessions/collab/mois" value={payload.estimated_sessions_per_collaborator_per_month} />
                </>
              ) : (
                <>
                  <KV label="Typologies" value={(payload.manager_typologies || []).join(', ')} />
                  <KV label="Niveau coach" value={payload.coach_requirement_level} />
                  <KV label="Budget" value={payload.budget_range} />
                  <KV label="Durée" value={`${payload.engagement_duration_months} mois`} />
                </>
              )}
              {payload.objectives?.length > 0 && <KV label="Objectifs" value={payload.objectives.join(', ')} span={2} />}
              {payload.business_objectives && <KV label="Objectifs business" value={payload.business_objectives} span={2} />}
              {payload.specialties_focus?.length > 0 && <KV label="Spécialités" value={payload.specialties_focus.join(', ')} span={2} />}
              {payload.notes && <KV label="Notes" value={payload.notes} span={2} />}
            </div>
          </section>

          {/* Quote builder / latest quote */}
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-display font-medium text-[#111827] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#92710A]" /> Devis
              </h2>
              {!latestQuote && qreq.status === 'pending_review' && (
                <button onClick={() => setShowQuoteBuilder(v => !v)}
                  data-testid="admin-quote-toggle-builder"
                  className="px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#111827] text-xs font-medium hover:bg-[#E5C15A] inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Rédiger un devis
                </button>
              )}
            </div>
            {showQuoteBuilder && !latestQuote && (
              <QuoteBuilder qreqId={id} token={token} onCreated={() => { setShowQuoteBuilder(false); fetchData(); }} />
            )}
            {latestQuote ? (
              <QuoteCard quote={latestQuote} token={token} onRefresh={fetchData} />
            ) : (
              !showQuoteBuilder && <p className="text-[13px] text-[#6B7280]">Aucun devis émis pour cette demande.</p>
            )}
          </section>

          {/* Comments */}
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-quote-comments">
            <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#92710A]" /> Commentaires internes ({comments.length})
            </h2>
            <div className="space-y-2 mb-3">
              {comments.map(c => (
                <div key={c.id} className="p-3 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8] text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-[#92710A] font-medium">{c.author_email}</span>
                    <span className="text-[11px] text-[#6B7280]">{new Date(c.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                  <p className="text-[13px] text-[#4B5563] whitespace-pre-wrap">{c.body}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <textarea rows={2} value={newComment} onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire interne…"
                data-testid="admin-quote-comment-input"
                className="flex-1 p-3 rounded-xl bg-[#F8F7F4] border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
              <button onClick={addComment}
                data-testid="admin-quote-comment-submit"
                className="px-4 rounded-xl bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937]">
                Publier
              </button>
            </div>
          </section>
        </div>

        {/* Right — CRM sidebar */}
        <aside className="space-y-4">
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5">
            <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-[#92710A]" /> Pilotage commercial
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium block mb-1.5">Priorité</label>
                <div className="flex gap-1.5 flex-wrap">
                  {['low', 'normal', 'high', 'urgent'].map(p => (
                    <button key={p} onClick={() => updatePriority(p)}
                      data-testid={`admin-quote-priority-${p}`}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium uppercase tracking-wider transition-all ${
                        qreq.commercial_priority === p ? 'bg-[#111827] text-white' : 'bg-[#F8F7F4] text-[#4B5563] hover:bg-[#EBEAE3]'
                      }`}>
                      {p === 'urgent' && <Flame className="w-3 h-3 inline mr-0.5" />} {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#6B7280] font-medium block mb-1.5">Prochaine action</label>
                <input value={qreq.next_action || ''}
                  onBlur={(e) => e.target.value !== qreq.next_action && updateAssignment(e.target.value)}
                  onChange={(e) => setData({ ...data, quote_request: { ...qreq, next_action: e.target.value } })}
                  data-testid="admin-quote-next-action"
                  className="w-full p-2 rounded-lg bg-[#F8F7F4] border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
              </div>
              <div className="text-[11px] text-[#6B7280]">
                <Clock className="w-3 h-3 inline mr-1" /> Créé le {new Date(qreq.created_at).toLocaleDateString('fr-FR')}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="bg-white border border-[#E8E5DE] rounded-2xl p-5" data-testid="admin-quote-timeline">
            <h2 className="text-sm font-display font-medium text-[#111827] mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#92710A]" /> Timeline ({timeline.length})
            </h2>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {timeline.map(e => (
                <div key={e.id} className="text-[12px] border-l-2 border-[#E8DFC8] pl-3">
                  <div className="text-[#111827] font-medium">{e.action}</div>
                  <div className="text-[#6B7280] text-[11px]">{new Date(e.timestamp).toLocaleString('fr-FR')} · {e.actor_email}</div>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

function KV({ label, value, span = 1 }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-medium">{label}</div>
      <div className="text-sm text-[#111827] mt-0.5">{value ?? '—'}</div>
    </div>
  );
}

function QuoteBuilder({ qreqId, token, onCreated }) {
  const [form, setForm] = useState({
    total_sessions: 100, session_rate_eur: 150, platform_commission_rate: 0.15,
    billing_mode: 'forfait', validity_days: 30, payment_terms_days: 30,
    commercial_conditions: '',
  });
  const [loading, setLoading] = useState(false);
  // 🔒 Iter246 — Options Insights (Prism/Pulse) injectées depuis la demande client
  const [insightsSuggestion, setInsightsSuggestion] = useState(null);
  const [insightsLines, setInsightsLines] = useState([]);  // éditables par l'admin
  const [insightsLoading, setInsightsLoading] = useState(true);

  // Pré-fetch suggestion Insights pour cette demande
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(
          `${API}/admin/enterprise/quote-requests/${qreqId}/insights-suggestion`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        setInsightsSuggestion(data);
        setInsightsLines((data.suggestions || []).map(s => ({ ...s })));
      } catch (_) { /* suggestion optionnelle */ }
      finally { if (alive) setInsightsLoading(false); }
    })();
    return () => { alive = false; };
  }, [qreqId, token]);

  const opts = insightsSuggestion?.insights_options || {};
  const hasPrism = Boolean(opts.individual_included);
  const hasPulse = Boolean(opts.collective_included);

  const updateInsightsLine = (idx, field, value) => {
    setInsightsLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: value };
      if (field === 'unit_price_eur' || field === 'quantity') {
        const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
        const unit = parseFloat(field === 'unit_price_eur' ? value : updated.unit_price_eur) || 0;
        updated.total_ht = Math.round(qty * unit * 100) / 100;
      }
      return updated;
    }));
  };

  const sessionsHT = (parseFloat(form.total_sessions) || 0) * (parseFloat(form.session_rate_eur) || 0);
  const insightsHT = insightsLines.reduce((s, l) => s + (parseFloat(l.total_ht) || 0), 0);
  const subtotal = sessionsHT + insightsHT;
  const ttc = subtotal * 1.20;

  const submit = async () => {
    // Vérification côté client : aligner avec le guard-fou backend
    const missing = [];
    if (hasPrism && !insightsLines.some(l => l.item_kind === 'insights_individual')) {
      missing.push('COACHDIA Prism');
    }
    if (hasPulse && !insightsLines.some(l => l.item_kind === 'insights_collective')) {
      missing.push('COACHDIA Pulse');
    }
    if (missing.length) {
      toast.error(`Le client a sélectionné : ${missing.join(', ')}. Ajoutez les lignes correspondantes.`);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form, quote_request_id: qreqId,
        additional_line_items: insightsLines.map(l => ({
          description: l.description,
          quantity: parseInt(l.quantity, 10) || 1,
          unit_price_eur: parseFloat(l.unit_price_eur) || 0,
          total_ht: parseFloat(l.total_ht) || 0,
          item_kind: l.item_kind,
          pricing_source: l.pricing_source || null,
        })),
      };
      const res = await fetch(`${API}/admin/enterprise/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(formatApiError(body, `Création du devis impossible (${res.status})`));
      }
      toast.success('Devis créé'); onCreated();
    } catch (e) { toast.error(formatApiError(e, 'Création du devis impossible')); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3 mt-3 p-4 rounded-xl bg-[#FBF9F3] border border-[#E8DFC8]" data-testid="quote-builder">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Input label="Nb sessions *" type="number" value={form.total_sessions}
          onChange={v => setForm({ ...form, total_sessions: v === '' ? '' : parseInt(v, 10) })} testid="qb-sessions" />
        <Input label="Tarif/session (€) *" type="number" step="0.01" value={form.session_rate_eur}
          onChange={v => setForm({ ...form, session_rate_eur: v === '' ? '' : parseFloat(v) })} testid="qb-rate" />
        <Input label="Commission plateforme *" type="number" step="0.01" min="0" max="1"
          value={form.platform_commission_rate}
          onChange={v => setForm({ ...form, platform_commission_rate: v === '' ? '' : parseFloat(v) })}
          testid="qb-commission"
          hint={`0.00 – 1.00 (ex: 0.15 = 15%). Valeur saisie ≈ ${Math.round(((form.platform_commission_rate || 0)) * 100)}%`} />
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">Mode facturation *</label>
          <select value={form.billing_mode} onChange={(e) => setForm({ ...form, billing_mode: e.target.value })}
            data-testid="qb-billing" className="w-full p-2 rounded-lg bg-white border border-[#E8E5DE] text-sm">
            <option value="forfait">Forfait</option>
            <option value="consumption">Consommation</option>
          </select>
        </div>
        <Input label="Validité (j) *" type="number" value={form.validity_days}
          onChange={v => setForm({ ...form, validity_days: v === '' ? '' : parseInt(v, 10) })} testid="qb-validity" />
        <Input label="Paiement (j) *" type="number" value={form.payment_terms_days}
          onChange={v => setForm({ ...form, payment_terms_days: v === '' ? '' : parseInt(v, 10) })} testid="qb-payment" />
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">Conditions commerciales</label>
        <textarea rows={3} value={form.commercial_conditions}
          onChange={(e) => setForm({ ...form, commercial_conditions: e.target.value })}
          data-testid="qb-conditions"
          placeholder="Démarrage, exclusivité, conditions particulières…"
          className="w-full p-3 rounded-xl bg-white border border-[#E8E5DE] text-sm" />
      </div>

      {/* 🔒 Iter246 — Bloc options Insights (Prism/Pulse) issues de la demande client */}
      {!insightsLoading && (hasPrism || hasPulse) && (
        <div className="p-3 rounded-xl bg-white border border-[#E8DFC8]" data-testid="qb-insights-block">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#92710A]" />
            <h4 className="text-sm font-medium text-[#111827]">Options COACHDIA sélectionnées par le client</h4>
          </div>
          <div className="text-[11px] text-[#6B7280] mb-3">
            Tarifs suggérés depuis <code>discovery_pricing_config</code> / <code>team_insights_pricing_config</code>.
            Modifiables ligne par ligne. <strong>Obligatoire dans le devis</strong>{' '}
            (sinon création refusée côté serveur).
          </div>
          {insightsLines.length === 0 && (
            <div className="flex items-start gap-2 text-xs p-2 bg-[#FEF3C7] rounded-md" data-testid="qb-insights-warning-empty">
              <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5" />
              <span>
                Aucune suggestion disponible ({hasPrism && 'Prism'}{hasPrism && hasPulse && ' + '}{hasPulse && 'Pulse'}{' '}
                coché(s)). Vérifiez `collaborators_count` / `team_size` sur la demande — ou ajoutez manuellement.
              </span>
            </div>
          )}
          <div className="space-y-2">
            {insightsLines.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end"
                   data-testid={`qb-insights-line-${li.item_kind}`}>
                <div className="col-span-6">
                  <label className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">
                    {li.item_kind === 'insights_collective' ? 'COACHDIA Pulse' : 'COACHDIA Prism'}
                  </label>
                  <input type="text" value={li.description}
                    onChange={(e) => updateInsightsLine(idx, 'description', e.target.value)}
                    className="w-full p-2 rounded-lg bg-[#FBF9F3] border border-[#E8E5DE] text-sm"
                    data-testid={`qb-insights-desc-${li.item_kind}`} />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">Qté</label>
                  <input type="number" min="1" value={li.quantity}
                    onChange={(e) => updateInsightsLine(idx, 'quantity', e.target.value)}
                    className="w-full p-2 rounded-lg bg-[#FBF9F3] border border-[#E8E5DE] text-sm"
                    data-testid={`qb-insights-qty-${li.item_kind}`} />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">PU HT €</label>
                  <input type="number" step="0.01" min="0" value={li.unit_price_eur}
                    onChange={(e) => updateInsightsLine(idx, 'unit_price_eur', e.target.value)}
                    className="w-full p-2 rounded-lg bg-[#FBF9F3] border border-[#E8E5DE] text-sm"
                    data-testid={`qb-insights-unit-${li.item_kind}`} />
                </div>
                <div className="col-span-2 text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Total HT</div>
                  <div className="text-sm font-medium text-[#111827] py-2">
                    {(parseFloat(li.total_ht) || 0).toLocaleString('fr-FR')} €
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-[#E8DFC8]">
        <div className="text-sm">
          <span className="text-[#6B7280]">Sessions : </span>
          <span className="text-[#111827]">{sessionsHT.toLocaleString('fr-FR')} €</span>
          {insightsHT > 0 && (
            <>
              <span className="text-[#6B7280] mx-1">+</span>
              <span className="text-[#6B7280]">Insights : </span>
              <span className="text-[#111827]">{insightsHT.toLocaleString('fr-FR')} €</span>
            </>
          )}
          <span className="text-[#6B7280] mx-2">·</span>
          <span className="text-[#6B7280]">Total HT : </span>
          <span className="text-[#111827] font-medium">{subtotal.toLocaleString('fr-FR')} €</span>
          <span className="text-[#6B7280] mx-2">·</span>
          <span className="text-[#6B7280]">TTC : </span>
          <span className="text-[#92710A] font-semibold">{ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
        </div>
        <button onClick={submit} disabled={loading}
          data-testid="qb-submit"
          className="px-5 py-2 rounded-full bg-[#111827] text-white text-sm font-medium hover:bg-[#1f2937] disabled:opacity-50 inline-flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Créer le devis
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', step, min, max, testid, hint }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#92710A] font-medium block mb-1">{label}</label>
      <input type={type} step={step} min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full p-2 rounded-lg bg-white border border-[#E8E5DE] text-sm focus:outline-none focus:border-[#92710A]" />
      {hint && <div className="text-[10px] text-[#6B7280] mt-1 leading-tight">{hint}</div>}
    </div>
  );
}

function QuoteCard({ quote, token, onRefresh }) {
  const [loading, setLoading] = useState(null);
  const meta = QUOTE_STATUS_META[quote.status] || {};
  const headers = { Authorization: `Bearer ${token}` };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/admin/enterprise/quotes/${quote.id}/pdf`, { headers });
      if (!res.ok) throw new Error('Impossible d\'ouvrir le PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e, 'Ouverture du PDF impossible')); }
  };
  const doAction = async (action, path) => {
    setLoading(action);
    try {
      const res = await fetch(`${API}/admin/enterprise/quotes/${quote.id}/${path}`, {
        method: 'POST', headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(formatApiError(body, `Action impossible (${res.status})`));
      }
      toast.success('Action appliquée'); onRefresh();
    } catch (e) { toast.error(formatApiError(e, 'Action impossible')); } finally { setLoading(null); }
  };
  const copyLink = async () => {
    // Build public view URL from origin
    const origin = window.location.origin;
    const url = `${origin}/enterprise/quote-view?token=${quote.signed_link_token}`;
    await navigator.clipboard.writeText(url);
    toast.success('Lien copié');
  };

  return (
    <div className="mt-2 p-4 rounded-xl border border-[#E8E5DE] bg-white" data-testid="admin-quote-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#92710A]" />
          <span className="text-sm font-display font-medium text-[#111827]">{quote.quote_number}</span>
          <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
            style={{ color: meta.color, backgroundColor: meta.bg }}>
            {meta.label}
          </span>
        </div>
        <div className="text-sm">
          <span className="text-[#6B7280]">Total TTC : </span>
          <span className="text-[#92710A] font-semibold">
            {quote.total_ttc?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px] py-3 border-y border-[#F0EEE8]">
        <div><span className="text-[#6B7280]">Sessions</span><div className="text-[#111827]">{quote.total_sessions}</div></div>
        <div><span className="text-[#6B7280]">Tarif/session</span><div className="text-[#111827]">{quote.session_rate_eur} €</div></div>
        <div><span className="text-[#6B7280]">Mode</span><div className="text-[#111827]">{quote.billing_mode}</div></div>
        <div><span className="text-[#6B7280]">Expire</span><div className="text-[#111827]">{new Date(quote.expires_at).toLocaleDateString('fr-FR')}</div></div>
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        <button onClick={downloadPdf} data-testid="admin-quote-pdf"
          className="px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] text-xs font-medium hover:bg-[#EBEAE3] inline-flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> PDF
        </button>
        <button onClick={copyLink} data-testid="admin-quote-copy-link"
          className="px-3 py-1.5 rounded-full bg-[#F8F7F4] border border-[#E8E5DE] text-[#4B5563] text-xs font-medium hover:bg-[#EBEAE3] inline-flex items-center gap-1.5">
          <Copy className="w-3.5 h-3.5" /> Copier lien signé
        </button>
        {quote.status === 'draft' && (
          <button onClick={() => doAction('send', 'send')} disabled={loading === 'send'}
            data-testid="admin-quote-send"
            className="px-3 py-1.5 rounded-full bg-[#111827] text-white text-xs font-medium hover:bg-[#1f2937] inline-flex items-center gap-1.5">
            {loading === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer l'email
          </button>
        )}
        {['accepted_online', 'bdc_received'].includes(quote.status) && (
          <button onClick={() => doAction('accept', 'confirm-acceptance')} disabled={loading === 'accept'}
            data-testid="admin-quote-confirm"
            className="px-3 py-1.5 rounded-full bg-[#059669] text-white text-xs font-medium hover:bg-[#047857] inline-flex items-center gap-1.5">
            {loading === 'accept' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Valider commande
          </button>
        )}
      </div>
      {quote.online_signature && (
        <div className="mt-3 p-3 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] text-[11px] text-[#047857]">
          <div className="font-medium mb-1">Signature en ligne enregistrée</div>
          <div>{quote.online_signature.signer_name} · {quote.online_signature.signer_email}</div>
          <div className="mt-1 font-mono text-[10px] truncate">Hash : {quote.online_signature.document_hash_sha256}</div>
        </div>
      )}
      {quote.bdc_upload && (
        <div className="mt-3 p-3 rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] text-[11px] text-[#047857]">
          <div className="font-medium mb-1">Bon de commande reçu</div>
          <div>Réf : {quote.bdc_upload.bdc_reference} · {quote.bdc_upload.filename}</div>
        </div>
      )}
      {quote.status === 'accepted' && quote.order_id && (
        <div className="mt-3">
          <Link to={`/admin/enterprise-orders/${quote.order_id}`}
            data-testid="admin-quote-link-order"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#065F46] text-white text-xs font-medium hover:bg-[#047857]">
            Accéder à la commande opérationnelle →
          </Link>
        </div>
      )}
    </div>
  );
}
